import React, { useState, useEffect } from "react";
import Modal from "./Modal";
import PrimaryButton from "../components/Buttons/PrimaryButton";
import SecondaryButton from "../components/Buttons/SecondaryButton";
import { getTags, addTagToDocument } from "../utils/Api";
import LoadingIcons from "react-loading-icons";

interface Tag {
  id: string;
  name: string;
  color: string;
}

interface AddTagsToDocumentsModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  documentIds: string[];
  onComplete: () => void;
}

const AddTagsToDocumentsModal: React.FC<AddTagsToDocumentsModalProps> = ({
  isOpen,
  setIsOpen,
  documentIds,
  onComplete,
}) => {
  const [tags, setTags] = useState<Tag[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchTags = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const { data } = await getTags();
        if (!cancelled) {
          const tagsArray = Array.isArray(data.tags) ? data.tags : [];
          setTags(tagsArray);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err.response?.data?.detail || "Failed to load tags");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    if (isOpen) {
      fetchTags();
      setSelectedTagIds(new Set());
      setError(null);
    }

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const fetchTags = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data } = await getTags();
      const tagsArray = Array.isArray(data.tags) ? data.tags : [];
      setTags(tagsArray);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to load tags");
    } finally {
      setIsLoading(false);
    }
  };

  const toggleTagSelection = (tagId: string) => {
    const newSelected = new Set(selectedTagIds);
    if (newSelected.has(tagId)) {
      newSelected.delete(tagId);
    } else {
      newSelected.add(tagId);
    }
    setSelectedTagIds(newSelected);
  };

  const handleSubmit = async () => {
    if (selectedTagIds.size === 0) {
      setError("Please select at least one tag");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const promises = [];
      for (const tagId of Array.from(selectedTagIds)) {
        for (const documentId of documentIds) {
          promises.push(addTagToDocument(tagId, documentId));
        }
      }

      await Promise.all(promises);
      setIsOpen(false);
      onComplete();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to add tags to documents");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} setIsOpen={setIsOpen}>
      <div className="sm:flex sm:items-start">
        <div className="w-full text-left">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">
            Add labels to {documentIds.length} document
            {documentIds.length !== 1 ? "s" : ""}
          </h3>

          {isLoading ? (
            <div className="flex justify-center items-center py-8">
              <LoadingIcons.TailSpin stroke="#1d365d" />
            </div>
          ) : tags.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p className="mb-4">
                No labels available. Create labels first to add them to
                documents.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* label list */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select labels
                </label>
                <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-md p-2 space-y-2">
                  {tags.map((tag) => (
                    <label
                      key={tag.id}
                      className={`flex items-center p-3 rounded-md cursor-pointer transition-colors ${
                        selectedTagIds.has(tag.id)
                          ? "bg-blue-50 border border-blue-200"
                          : "bg-gray-50 hover:bg-gray-100 border border-gray-200"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedTagIds.has(tag.id)}
                        onChange={() => toggleTagSelection(tag.id)}
                        className="w-4 h-4 mr-3 cursor-pointer"
                        disabled={isSubmitting}
                      />
                      <span
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
                        style={{
                          backgroundColor: `${tag.color}20`,
                          color: tag.color,
                          border: `1px solid ${tag.color}40`,
                        }}
                      >
                        {tag.name}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {selectedTagIds.size > 0 && (
                <div className="text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded-md p-2">
                  {selectedTagIds.size} label
                  {selectedTagIds.size !== 1 ? "s" : ""} selected
                </div>
              )}

              {error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2">
                  {error}
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <SecondaryButton
                  onClick={() => setIsOpen(false)}
                  disabled={isSubmitting}
                  className="flex-1"
                >
                  Cancel
                </SecondaryButton>

                <PrimaryButton
                  onClick={handleSubmit}
                  disabled={isSubmitting || selectedTagIds.size === 0}
                  className="flex-1"
                >
                  {isSubmitting ? "Adding labels..." : "Add labels"}
                </PrimaryButton>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AddTagsToDocumentsModal;
